import type {
  XbotCapabilities, XbotNameOp, XbotSuggestion, XbotSuggestRequest, XbotSuggestResponse, XbotSuggestScope, XbotSuggestSelected,
  XbotSuggestSource, XbotUtf16Range,
} from "@dto/pyxbot-agent.dto";
import type { XbotRefused } from "../agent/chatClient";
import type { XbotAborted } from "../xbotFetch";
import { DRAFT_MESSAGE_MAX, FRAGMENT_MAX, FragmentAt } from "./draft";
import { Label } from "src/data/Label";

// Plain-data state machine for when the editor asks for objects. Debounced ~250ms after typing,
// one request in flight per draft; a stale answer (word moved, cursor left it, 429/501) is dropped.

export const SUGGEST_DELAY_MS = 250;
export const SUGGEST_MAX_DELAY_MS = 2000;

/** Typing asks from two characters on; the button asks for anything. */
export const AUTO_MIN_CHARS = 2;

const REQUEST_ID_MAX = 999_999_999;

/** What became of one request; the client never throws. */
export type SuggestOutcome = { kind: "ok"; response: XbotSuggestResponse } | XbotRefused | XbotAborted;

/** Words to ask for, and whether the person asked with the button (then the answer opens even empty). */
export interface Want {
  fragment: string;
  range: XbotUtf16Range;
  manual: boolean;
}

/** The answer on screen and the words it was asked for. */
export interface Offer {
  requestId: number;
  fragment: string;
  range: XbotUtf16Range;
  items: XbotSuggestion[];
  sources: XbotSuggestResponse["sources"];
  partial: boolean;
  manual: boolean;
  scope: XbotSuggestScope;
}

export type QuietState = "unavailable" | "ng_silent" | null;

export interface SuggestFlow {
  draftId: string;
  /** The newest request_id given out for this draft. */
  lastRequestId: number;
  inFlight: { requestId: number; want: Want } | null;
  /** Words waiting to be asked for. */
  want: Want | null;
  delayMs: number;
  /** The service does not offer objects (501): nothing is asked again in this session. */
  offForSession: boolean;
  /** The service does not hold this conversation for the person: nothing is asked again for this draft. */
  offForDraft: boolean;
  quiet: QuietState;
  /** Words the person shut the offer for with Escape; it stays shut for them until they change. */
  dismissed: { fragment: string; start: number } | null;
  scope: XbotSuggestScope;
  /** Where the word the wider scope was asked for starts; another word goes back to the narrow scope. */
  scopeStart: number | null;
  offer: Offer | null;
  /** The active option, -1 for none. */
  active: number;
}

export interface FlowStep {
  flow: SuggestFlow;
  wait: number | null;
  send: XbotSuggestRequest | null;
  abort: boolean;
  unsupported: boolean;
}

/** What a request needs besides the words: the conversation, and the object picked before them. */
export interface AskContext {
  threadId: string | null;
  selected: XbotSuggestSelected | null;
}

function step(flow: SuggestFlow, more: Partial<Omit<FlowStep, "flow">> = {}): FlowStep {
  return { flow, wait: null, send: null, abort: false, unsupported: false, ...more };
}

export function startFlow(draftId: string): SuggestFlow {
  return {
    draftId,
    lastRequestId: 0,
    inFlight: null,
    want: null,
    delayMs: SUGGEST_DELAY_MS,
    offForSession: false,
    offForDraft: false,
    quiet: null,
    dismissed: null,
    scope: "last_answer",
    scopeStart: null,
    offer: null,
    active: -1,
  };
}

/** A new draft (the message went, or the editor was emptied): numbering starts again, what is out is dropped. */
export function forDraft(flow: SuggestFlow, draftId: string): FlowStep {
  const fresh = startFlow(draftId);
  return step({ ...fresh, offForSession: flow.offForSession, delayMs: flow.delayMs }, { abort: Boolean(flow.inFlight) });
}

function closed(flow: SuggestFlow): SuggestFlow {
  return { ...flow, offer: null, active: -1 };
}

function sameWords(a: { fragment: string; start: number } | null, fragment: string, start: number): boolean {
  return Boolean(a) && a?.fragment === fragment && a?.start === start;
}

// Null when there's nothing to offer for (blank, picked name). Drops the in-flight request once
// the cursor leaves the word it was asked for, so a stale answer can't open over a new word.
export function typed(flow: SuggestFlow, at: FragmentAt | null): FlowStep {
  const out = flow.inFlight;
  if (out && at?.start !== out.want.range.start) {
    return { ...typedAt({ ...flow, inFlight: null }, at), abort: true };
  }
  return typedAt(flow, at);
}

function typedAt(flow: SuggestFlow, at: FragmentAt | null): FlowStep {
  if (flow.offForSession || flow.offForDraft || !at) {
    return step({ ...closed(flow), want: null });
  }
  let next: SuggestFlow = flow;
  if (next.scopeStart !== null && next.scopeStart !== at.start) {
    next = { ...next, scope: "last_answer", scopeStart: null };
  }
  if (next.offer && next.offer.range.start !== at.start) {
    next = closed(next);
  } else if (next.offer && next.offer.fragment !== at.fragment) {
    next = { ...next, active: -1 };
  }
  if (next.dismissed) {
    if (sameWords(next.dismissed, at.fragment, at.start)) {
      return step({ ...next, want: null });
    }
    next = { ...next, dismissed: null };
  }
  if ([...at.fragment].length < AUTO_MIN_CHARS) {
    return step({ ...closed(next), want: null });
  }
  const shown = next.offer && sameWords({ fragment: next.offer.fragment, start: next.offer.range.start }, at.fragment, at.start);
  const out = next.inFlight && sameWords({ fragment: next.inFlight.want.fragment, start: next.inFlight.want.range.start }, at.fragment, at.start);
  if (shown || out) {
    return step({ ...next, want: null });
  }
  const want: Want = { fragment: at.fragment, range: { start: at.start, end: at.end }, manual: false };
  return step({ ...next, want }, { wait: next.delayMs });
}

// Matches the service's own limits (message length, fragment length) so it isn't asked something it would just refuse.
function askable(want: Want): boolean {
  return want.range.end <= DRAFT_MESSAGE_MAX && want.fragment.length <= FRAGMENT_MAX;
}

/** One request for the words at a range; the offer while typing and the Objects search differ in how and where they look. */
function request(
  draftId: string, requestId: number, threadId: string, words: Pick<Want, "fragment" | "range">, nameOp: XbotNameOp,
  scope: XbotSuggestScope, selected: XbotSuggestSelected | null,
): XbotSuggestRequest {
  return {
    draft_id: draftId,
    request_id: requestId,
    thread_id: threadId,
    fragment: words.fragment,
    range: { start: words.range.start, end: words.range.end },
    mode: "auto",
    name_op: nameOp,
    filter: { scope, platform: null, kind: null, class: null },
    selected,
  };
}

/** The answer to a request as the list shows it. */
function shownOffer(sent: XbotSuggestRequest, response: XbotSuggestResponse, manual: boolean): Offer {
  return {
    requestId: sent.request_id,
    fragment: sent.fragment,
    range: { start: sent.range.start, end: sent.range.end },
    items: response.suggestions,
    sources: response.sources,
    partial: Boolean(response.partial),
    manual,
    scope: sent.filter.scope,
  };
}

function issue(flow: SuggestFlow, want: Want, ctx: AskContext, abort = false): FlowStep {
  const requestId = flow.lastRequestId + 1;
  if (!ctx.threadId || requestId > REQUEST_ID_MAX || !askable(want)) {
    return step({ ...flow, want: null }, { abort });
  }
  const send = request(flow.draftId, requestId, ctx.threadId, want, "prefix", flow.scope, ctx.selected);
  return step({ ...flow, lastRequestId: requestId, inFlight: { requestId, want }, want: null }, { send, abort });
}

/** The wait after the last key is over: ask, unless a request is still out - then the words wait for its answer. */
export function due(flow: SuggestFlow, ctx: AskContext): FlowStep {
  if (!flow.want || flow.inFlight || flow.offForSession || flow.offForDraft) {
    return step(flow);
  }
  return issue(flow, flow.want, ctx);
}

// A manual ask (button, search-all, pick-again) goes immediately regardless of length or a prior
// Escape dismissal, dropping any request still out.
export function askNow(flow: SuggestFlow, at: FragmentAt | null, ctx: AskContext, scope?: XbotSuggestScope): FlowStep {
  if (flow.offForSession || flow.offForDraft || !at) {
    return step(flow);
  }
  let next: SuggestFlow = { ...flow, dismissed: null, inFlight: null };
  if (scope) {
    next = { ...next, scope, scopeStart: scope === "all" ? at.start : null };
  } else if (next.scopeStart !== null && next.scopeStart !== at.start) {
    next = { ...next, scope: "last_answer", scopeStart: null };
  }
  const want: Want = { fragment: at.fragment, range: { start: at.start, end: at.end }, manual: true };
  return issue(next, want, ctx, Boolean(flow.inFlight));
}

// An answer counts only if its words still stand where asked, and (for a typed request) the cursor
// is still in them; a manual ask opens wherever the cursor went.
export function answered(
  flow: SuggestFlow,
  sent: XbotSuggestRequest,
  outcome: SuggestOutcome,
  text: string,
  caretWord?: FragmentAt | null,
): FlowStep {
  if (sent.draft_id !== flow.draftId) {
    return step(flow);
  }
  const mine = flow.inFlight?.requestId === sent.request_id ? flow.inFlight : null;
  let next: SuggestFlow = mine ? { ...flow, inFlight: null } : flow;
  const asked: Want = mine?.want ?? { fragment: sent.fragment, range: sent.range, manual: false };
  // A dropped request's answer doesn't hurry the next one; it still waits out its own delay.
  const more = (ms: number) => (mine && next.want ? ms : null);

  if (outcome.kind === "aborted") {
    return step(next, { wait: more(0) });
  }

  if (outcome.kind === "refused") {
    const { status, error } = outcome;
    if (status === 429) {
      const delayMs = Math.min(next.delayMs * 2, SUGGEST_MAX_DELAY_MS);
      next = { ...next, delayMs, want: next.want ?? (mine ? asked : null) };
      return step(next, { wait: more(delayMs) });
    }
    if (status === 501) {
      return step({ ...closed(next), offForSession: true, want: null }, { unsupported: true });
    }
    if (status === 400 && error.code === "ref_not_in_context") {
      return step({ ...closed(next), offForDraft: true, want: null });
    }
    if (status === 0 || status >= 500) {
      return step({ ...closed(next), quiet: "unavailable" }, { wait: more(next.delayMs) });
    }
    return step(closed(next), { wait: more(next.delayMs) });
  }

  const response = outcome.response;
  next = { ...next, delayMs: SUGGEST_DELAY_MS, quiet: null };
  const late = !mine
    || response.draft_id !== flow.draftId
    || response.request_id !== sent.request_id
    || response.request_id < flow.lastRequestId
    || text.slice(sent.range.start, sent.range.end) !== sent.fragment
    || (!asked.manual && caretWord !== undefined && caretWord?.start !== sent.range.start);
  if (late) {
    return step(next, { wait: more(0) });
  }
  if (!asked.manual && sameWords(flow.dismissed, sent.fragment, sent.range.start)) {
    return step(next, { wait: more(0) });
  }
  const ngState = response.sources.ng?.state;
  const ngSilent = ngState === "error" || ngState === "timeout";
  if (!response.suggestions.length && !asked.manual) {
    return step({ ...closed(next), quiet: ngSilent ? "ng_silent" : null }, { wait: more(0) });
  }
  // With the offer open, the offer itself says what NG did; the quiet line stays for when nothing opens.
  return step({ ...next, offer: shownOffer(sent, response, asked.manual), active: -1, quiet: null }, { wait: more(0) });
}

/** Escape: the offer shuts and stays shut for these words. */
export function dismiss(flow: SuggestFlow): SuggestFlow {
  const words = flow.offer
    ? { fragment: flow.offer.fragment, start: flow.offer.range.start }
    : flow.want ? { fragment: flow.want.fragment, start: flow.want.range.start } : flow.dismissed;
  return { ...closed(flow), want: null, dismissed: words };
}

// Shuts the offer without remembering it (pick made, focus lost, undo, message sent), aborting any request still out.
export function closeOffer(flow: SuggestFlow): FlowStep {
  return step({ ...closed(flow), want: null, inFlight: null }, { abort: Boolean(flow.inFlight) });
}

// True when every source skipped for being too short — distinct from "nothing found", which implies a search happened.
function tooShortToSearch(offer: Offer): boolean {
  const sources = offer.sources;
  return [sources.last_answer, sources.thread, sources.ng]
    .every((source) => source?.state === "skipped" && source.reason === "too_short");
}

// No search-all row for words too short to search — all of NG would refuse them the same way.
export function offersSearchAll(flow: SuggestFlow): boolean {
  const offer = flow.offer;
  return offer !== null && offer.scope === "last_answer" && !tooShortToSearch(offer);
}

/** The options the arrows walk through: the objects, then "Search all of XorMon" when it is there. */
export function optionCount(flow: SuggestFlow): number {
  if (!flow.offer) {
    return 0;
  }
  return flow.offer.items.length + (offersSearchAll(flow) ? 1 : 0);
}

export function moveActive(flow: SuggestFlow, delta: number): SuggestFlow {
  const count = optionCount(flow);
  if (!count) {
    return flow;
  }
  if (flow.active < 0) {
    return { ...flow, active: delta > 0 ? 0 : count - 1 };
  }
  return { ...flow, active: (((flow.active + delta) % count) + count) % count };
}

/** The key as the editor saw it. */
export interface KeyLike {
  key: string;
  code?: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  isComposing?: boolean;
}

export type KeyIntent = "none" | "undo" | "redo" | "next" | "previous" | "accept" | "searchAll" | "close" | "search";

// IME composition and Tab are left alone; undo/redo are the chat's own since text+picks move together;
// arrows/Enter/Escape belong to the offer only while it's open, and Enter only with an option active.
export function keyIntent(key: KeyLike, flow: SuggestFlow): KeyIntent {
  if (key.isComposing) {
    return "none";
  }
  const name = (key.key || "").toLowerCase();
  const command = (key.ctrlKey || key.metaKey) && !key.altKey;
  if (command && (name === "z" || key.code === "KeyZ")) {
    return key.shiftKey ? "redo" : "undo";
  }
  if (key.ctrlKey && !key.metaKey && !key.altKey && !key.shiftKey && (name === "y" || key.code === "KeyY")) {
    return "redo";
  }
  // Ctrl+Space opens the Objects search, the IDEs' "suggest"; ⌥Space too, as ⌃Space switches the keyboard on a Mac
  const space = key.code === "Space" || key.key === " ";
  const onlyCtrl = key.ctrlKey && !key.metaKey && !key.altKey && !key.shiftKey;
  const onlyAlt = key.altKey && !key.ctrlKey && !key.metaKey && !key.shiftKey;
  if (space && (onlyCtrl || onlyAlt)) {
    return "search";
  }
  if (!flow.offer) {
    return "none";
  }
  const plain = !key.shiftKey && !key.ctrlKey && !key.metaKey && !key.altKey;
  if (key.key === "Escape") {
    return "close";
  }
  if (!plain || !optionCount(flow)) {
    return "none";
  }
  if (key.key === "ArrowDown") {
    return "next";
  }
  if (key.key === "ArrowUp") {
    return "previous";
  }
  if (key.key === "Enter" && flow.active >= 0) {
    return offersSearchAll(flow) && flow.active === flow.offer.items.length ? "searchAll" : "accept";
  }
  return "none";
}

export const SUGGEST_TEXTS = {
  button: "Find an object by name and add it to your question (Ctrl+Space)",
  needsUpdate: `${Label.XORBOT} service needs updating`,
  checking: `Checking whether the ${Label.XORBOT} service can suggest objects`,
  noCaret: "Suggestions need the plain text editor",
  unavailable: "Suggestions unavailable",
  ngSilent: "XorMon did not answer",
  more: "…and more, type more",
  anywhere: "XorMon matched anywhere in the name",
  nothing: "No object of that name was found.",
  tooShort: `The words are too short to search: type at least ${AUTO_MIN_CHARS} characters.`,
  searchAll: "Search all of XorMon",
};

// Stays visible and says why when disabled, rather than hiding — a hidden button is a bug nobody would report.
export function buttonState(
  capabilities: XbotCapabilities | null,
  flow: SuggestFlow,
  caretReadable: boolean,
): { disabled: boolean; reason: string } {
  if (!caretReadable) {
    return { disabled: true, reason: SUGGEST_TEXTS.noCaret };
  }
  if (!capabilities) {
    return { disabled: true, reason: SUGGEST_TEXTS.checking };
  }
  if (!capabilities.suggest || !capabilities.bindings || flow.offForSession) {
    return { disabled: true, reason: SUGGEST_TEXTS.needsUpdate };
  }
  return { disabled: false, reason: SUGGEST_TEXTS.button };
}

/** The quiet line under the input, or null. */
export function quietText(flow: SuggestFlow): string | null {
  if (flow.quiet === "unavailable") {
    return SUGGEST_TEXTS.unavailable;
  }
  if (flow.quiet === "ng_silent") {
    return SUGGEST_TEXTS.ngSilent;
  }
  return null;
}

// Distinguishes nothing-found / NG-didn't-answer / too-short-to-search, plus a partial-results and looser-match note.
export function offerNotes(offer: Offer | null): string[] {
  if (!offer) {
    return [];
  }
  const notes: string[] = [];
  const ng = offer.sources.ng?.state;
  const ngFailed = ng === "error" || ng === "timeout";
  if (!offer.items.length) {
    if (ngFailed) {
      notes.push(SUGGEST_TEXTS.ngSilent);
    } else {
      notes.push(tooShortToSearch(offer) ? SUGGEST_TEXTS.tooShort : SUGGEST_TEXTS.nothing);
    }
  } else {
    if (offer.partial) {
      notes.push(SUGGEST_TEXTS.more);
    }
    if (ngFailed) {
      notes.push(SUGGEST_TEXTS.ngSilent);
    }
  }
  const filter = offer.sources.ng?.name_filter;
  if (filter?.confirmed === false && filter.asked !== "contains") {
    notes.push(SUGGEST_TEXTS.anywhere);
  }
  return notes;
}

const GROUP_TITLES: Record<XbotSuggestSource, string> = {
  last_answer: "From the last answer",
  thread: "In this conversation",
  ng: "Found in XorMon",
};

export interface OfferGroup {
  source: XbotSuggestSource;
  title: string;
  options: { index: number; item: XbotSuggestion }[];
}

// Groups options by source, a new heading whenever it changes, keeping exactly the service's own order.
export function offerGroups(offer: Offer | null): OfferGroup[] {
  const groups: OfferGroup[] = [];
  for (const [index, item] of (offer?.items ?? []).entries()) {
    const last = groups[groups.length - 1];
    if (last?.source === item.source) {
      last.options.push({ index, item });
    } else {
      groups.push({ source: item.source, title: GROUP_TITLES[item.source] ?? GROUP_TITLES.ng, options: [{ index, item }] });
    }
  }
  return groups;
}

// The Objects button's search (ObjectSearch.tsx): a field of its own where a name is typed, whatever the cursor
// stands on. It asks the same service as the offer while typing, with a draft id of its own, so its requests never
// mix with that offer's count. The service only searches the words; where the pick goes is the window's (the range
// sent is where it will be put, and comes back unchanged).

export type SearchStatus = "idle" | "short" | "reading" | "ready" | "unavailable";

export interface SearchState {
  /** New for every opening of the search. */
  draftId: string;
  lastRequestId: number;
  offer: Offer | null;
  /** The object the arrows stand on; -1 none. */
  active: number;
  status: SearchStatus;
}

export function startSearch(draftId: string): SearchState {
  return { draftId, lastRequestId: 0, offer: null, active: -1, status: "idle" };
}

export interface SearchContext {
  threadId: string | null;
  /** The object picked nearest before where the pick will go: objects of its platform and kind come first. */
  selected: XbotSuggestSelected | null;
  /** Where in the editor's text the pick will go. */
  start: number;
}

export interface SearchStep {
  state: SearchState;
  /** The request to send now, or null. */
  send: XbotSuggestRequest | null;
}

/** The words asked for: the field's text without spaces at its ends. */
export function searchWords(text: string): string {
  return (text || "").trim();
}

/** Asks for the typed words, unless they are too short or too long to search (the service would only refuse them). */
export function askSearch(state: SearchState, text: string, ctx: SearchContext): SearchStep {
  const fragment = searchWords(text);
  if ([...fragment].length < AUTO_MIN_CHARS || fragment.length > FRAGMENT_MAX) {
    return { state: { ...state, offer: null, active: -1, status: fragment ? "short" : "idle" }, send: null };
  }
  const requestId = state.lastRequestId + 1;
  if (!ctx.threadId || requestId > REQUEST_ID_MAX) {
    return { state: { ...state, status: "unavailable" }, send: null };
  }
  // The range only says where the pick will go (the window puts it there itself) and the service refuses one ending
  // past the message's limit, so near the limit it is drawn back to end there.
  const start = Math.min(Math.max(0, ctx.start), DRAFT_MESSAGE_MAX - fragment.length);
  // Anywhere in the name ("07" finds esxi-07.lab; the service ranks an exact name and a name's start first), and
  // everywhere at once: all of NG, the last answer and the conversation (the service still puts those first), so
  // the search needs no "Search all of XorMon" row.
  const send = request(state.draftId, requestId, ctx.threadId, { fragment, range: { start, end: start + fragment.length } }, "contains", "all", ctx.selected);
  return { state: { ...state, lastRequestId: requestId, status: "reading" }, send };
}

/** The service's answer: only the newest request's counts; an older one is dropped. */
export function searchAnswered(state: SearchState, sent: XbotSuggestRequest, outcome: SuggestOutcome): SearchState {
  if (sent.draft_id !== state.draftId || sent.request_id !== state.lastRequestId || outcome.kind === "aborted") {
    return state;
  }
  if (outcome.kind === "refused") {
    return { ...state, offer: null, active: -1, status: "unavailable" };
  }
  if (outcome.response.request_id !== sent.request_id) {
    return state;
  }
  // asked for on purpose: an empty answer is said, not hidden
  return { ...state, offer: shownOffer(sent, outcome.response, true), active: -1, status: "ready" };
}

/** The arrows move over the objects, and from the last back to none. */
export function moveSearchActive(state: SearchState, delta: number): SearchState {
  const count = state.offer?.items.length ?? 0;
  if (!count) {
    return state;
  }
  const next = state.active + delta;
  return { ...state, active: next < -1 ? count - 1 : next >= count ? -1 : next };
}
